import React, { useState, useEffect } from "react";
import { ArrowLeft, Users, Calendar, BarChart3, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function DailyActiveUsersChart() {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${API_BASE}/admin/daily-active-users?month=${month}`);
        const data = await res.json();
        if (isMounted) {
          setChartData(data.data || []);
        }
      } catch (err) {
        console.error("Failed to load daily active users:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [authFetch, month]);

  const maxUsers = Math.max(...chartData.map(d => d.active_users), 10); // Minimum scale of 10
  const totalUsersInMonth = chartData.reduce((sum, d) => sum + d.active_users, 0);
  const avgUsersPerDay = chartData.length > 0 ? (totalUsersInMonth / chartData.length).toFixed(1) : 0;

  // Generate days in month for empty slots
  const [yearStr, monthStr] = month.split("-");
  const daysInMonth = new Date(yearStr, monthStr, 0).getDate();
  const fullChartData = Array.from({ length: daysInMonth }, (_, i) => {
    const day = String(i + 1).padStart(2, "0");
    const dateStr = `${month}-${day}`;
    const existing = chartData.find(d => d.date === dateStr);
    return existing || { date: dateStr, active_users: 0 };
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <button 
          onClick={() => navigate("/")}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={20} />
            สถิติผู้ใช้งานรายวัน
          </h1>
          <p className="text-xs text-slate-500 font-medium">ดูจำนวนผู้ใช้งาน AI ในแต่ละวัน</p>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-6 max-w-5xl w-full mx-auto space-y-6">
        
        {/* Controls & Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex flex-col justify-center">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Calendar size={14} className="text-violet-500" />
              เลือกเดือน
            </div>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none transition-all"
            />
          </div>
          
          <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-5 border border-blue-500 shadow-lg shadow-blue-500/20 text-white flex flex-col justify-center relative overflow-hidden">
            <div className="absolute right-[-10%] top-[-20%] w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
            <div className="text-xs font-medium text-blue-100 uppercase tracking-wider mb-1 flex items-center gap-2">
              <Users size={14} />
              รวมผู้ใช้งาน (สะสม)
            </div>
            <div className="text-3xl font-extrabold">{totalUsersInMonth} <span className="text-sm font-medium text-blue-200">ครั้ง</span></div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex flex-col justify-center">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-2">
              <Activity size={14} className="text-emerald-500" />
              เฉลี่ยต่อวัน
            </div>
            <div className="text-3xl font-extrabold text-slate-800">{avgUsersPerDay} <span className="text-sm font-medium text-slate-400">คน/วัน</span></div>
          </div>
        </div>

        {/* Chart Area */}
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-base font-bold text-slate-800">กราฟผู้ใช้งานรายวัน</h3>
            <div className="text-xs font-medium text-slate-500 bg-slate-100 px-3 py-1 rounded-full">เดือน {month}</div>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="relative h-72 w-full mt-4">
              {/* Y-Axis Lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-full border-t border-slate-100 border-dashed flex-1 flex items-start">
                    <span className="text-[10px] text-slate-400 -mt-2 -ml-6 w-5 text-right">
                      {Math.round(maxUsers - (maxUsers / 4) * i)}
                    </span>
                  </div>
                ))}
                <div className="w-full border-t border-slate-200">
                  <span className="text-[10px] text-slate-400 -mt-2 -ml-6 w-5 text-right block">0</span>
                </div>
              </div>

              {/* Bars */}
              <div className="absolute inset-0 ml-2 flex items-end justify-between gap-1 sm:gap-2 pb-[1px]">
                {fullChartData.map((d, i) => {
                  const heightPercent = (d.active_users / maxUsers) * 100;
                  const dayNum = d.date.split("-")[2];
                  const isWeekend = new Date(d.date).getDay() === 0 || new Date(d.date).getDay() === 6;
                  
                  return (
                    <div key={i} className="relative flex-1 flex flex-col items-center justify-end h-full group">
                      {/* Tooltip */}
                      <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-[10px] py-1 px-2 rounded-lg whitespace-nowrap z-10 pointer-events-none shadow-lg">
                        วันที่ {dayNum}: <span className="font-bold">{d.active_users} คน</span>
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45"></div>
                      </div>
                      
                      {/* Bar */}
                      <div 
                        className={`w-full max-w-[24px] rounded-t-sm transition-all duration-500 ease-out group-hover:opacity-80 cursor-pointer
                          ${d.active_users > 0 ? 'bg-gradient-to-t from-blue-600 to-cyan-400 shadow-[0_0_10px_rgba(59,130,246,0.2)]' : 'bg-transparent'}
                        `}
                        style={{ height: `${Math.max(heightPercent, 0)}%`, minHeight: d.active_users > 0 ? '4px' : '0' }}
                      />
                      
                      {/* X-Axis Label */}
                      <div className={`absolute -bottom-6 text-[9px] font-medium ${isWeekend ? 'text-rose-400' : 'text-slate-400'} ${i % 2 !== 0 ? 'hidden sm:block' : ''}`}>
                        {dayNum}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
      </main>
    </div>
  );
}
